import React from "react";

/** Shared solid chrome for Payroll Actions / F&F. */
export const PAYROLL_ACTION_CHROME = {
  accent: "hsl(var(--ds-primary))",
  stripeClass: "h-1 w-full bg-[hsl(var(--ds-primary))]",
  borderClass: "border-[hsl(var(--ds-primary)/0.45)]",
  cardBorderClass: "border-2 border-[hsl(var(--ds-primary)/0.35)]",
  softBg: "bg-[hsl(var(--ds-primary)/0.06)]",
};

/**
 * Crisp isometric icons — no drop-shadow / muddy shade; fills placeholder.
 */
function SvgShell({ children, title, size = 40 }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className="payroll-action-3d-icon block h-full w-full max-h-full max-w-full shrink-0"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      style={{ opacity: size <= 24 ? 0.9 : 1 }}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

export function Icon3dStructure({ size = 40 } = {}) {
  return (
    <SvgShell title="Assigned Structure" size={size}>
      <path d="M8 24 L32 10 L56 24 L32 38 Z" fill="#5EEAD4" />
      <path d="M8 24 L32 38 L32 56 L8 42 Z" fill="#0F766E" />
      <path d="M56 24 L32 38 L32 56 L56 42 Z" fill="#14B8A6" />
      <path d="M20 30 L32 23 L44 30 L32 37 Z" fill="#ECFDF5" />
      <rect x="26" y="40" width="12" height="8" rx="1" fill="#F0FDFA" />
    </SvgShell>
  );
}

export function Icon3dRevision({ size = 40 } = {}) {
  return (
    <SvgShell title="Salary Revision" size={size}>
      <path d="M12 52 L32 8 L52 52 Z" fill="#EA580C" />
      <path d="M20 46 L32 18 L44 46 Z" fill="#FB923C" />
      <circle cx="32" cy="38" r="8" fill="#FFF7ED" />
      <path d="M32 32 v12 M26 38 h12" stroke="#C2410C" strokeWidth="3" strokeLinecap="round" />
      <path d="M44 12 l8-1 -1 7 5 5 -7 1 -2 7 -4-5 -7 2 2-7 -5-4 7-1 Z" fill="#FBBF24" />
    </SvgShell>
  );
}

export function Icon3dStatutory({ size = 40 } = {}) {
  return (
    <SvgShell title="Update Statutory" size={size}>
      <path d="M14 50 V22 l18-12 18 12 v28 Z" fill="#1D4ED8" />
      <path d="M14 22 l18 12 18-12" fill="#60A5FA" />
      <path d="M32 34 v20" stroke="#DBEAFE" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="32" cy="28" r="5" fill="#EFF6FF" />
      <path d="M22 44 h20" stroke="#93C5FD" strokeWidth="2.5" strokeLinecap="round" />
    </SvgShell>
  );
}

export function Icon3dTax({ size = 40 } = {}) {
  return (
    <SvgShell title="Tax Planning" size={size}>
      <rect x="10" y="18" width="36" height="34" rx="4" fill="#6D28D9" />
      <rect x="16" y="12" width="36" height="34" rx="4" fill="#8B5CF6" />
      <rect x="22" y="20" width="24" height="4" rx="1" fill="#EDE9FE" />
      <rect x="22" y="28" width="16" height="3" rx="1" fill="#DDD6FE" />
      <text x="24" y="46" fill="#F5F3FF" fontSize="16" fontWeight="800" fontFamily="system-ui">
        ₹
      </text>
    </SvgShell>
  );
}

export function Icon3dLoan({ size = 40 } = {}) {
  return (
    <SvgShell title="Loan" size={size}>
      <ellipse cx="32" cy="46" rx="22" ry="11" fill="#A16207" />
      <ellipse cx="32" cy="38" rx="22" ry="11" fill="#EAB308" />
      <ellipse cx="32" cy="30" rx="17" ry="9" fill="#FEF08A" />
      <ellipse cx="32" cy="28" rx="10" ry="5" fill="#FFFBEB" />
      <text x="25" y="32" fill="#854D0E" fontSize="12" fontWeight="800" fontFamily="system-ui">
        ₹
      </text>
    </SvgShell>
  );
}

export function Icon3dEncash({ size = 40 } = {}) {
  return (
    <SvgShell title="Leave Encashment" size={size}>
      <rect x="20" y="8" width="20" height="30" rx="3" fill="#047857" transform="rotate(-12 30 23)" />
      <rect x="16" y="14" width="26" height="34" rx="3" fill="#10B981" />
      <rect x="22" y="22" width="14" height="3" rx="1" fill="#ECFDF5" />
      <rect x="22" y="30" width="10" height="3" rx="1" fill="#D1FAE5" />
      <circle cx="46" cy="46" r="12" fill="#F59E0B" />
      <circle cx="46" cy="46" r="7" fill="#FDE68A" />
    </SvgShell>
  );
}

export function Icon3dAdvance({ size = 40 } = {}) {
  return (
    <SvgShell title="Advance" size={size}>
      <rect x="8" y="30" width="48" height="22" rx="5" fill="#C2410C" />
      <rect x="8" y="18" width="48" height="22" rx="5" fill="#F97316" />
      <rect x="8" y="18" width="48" height="8" fill="#EA580C" />
      <circle cx="44" cy="34" r="8" fill="#FDE68A" />
      <circle cx="44" cy="34" r="4" fill="#F59E0B" />
      <rect x="16" y="24" width="14" height="3" rx="1" fill="#FFEDD5" />
    </SvgShell>
  );
}

export function Icon3dReimburse({ size = 40 } = {}) {
  return (
    <SvgShell title="Reimbursement" size={size}>
      <path d="M12 14 h26 l12 12 v30 H12 Z" fill="#166534" />
      <path d="M14 10 h26 l12 12 v30 H14 Z" fill="#22C55E" />
      <path d="M40 10 v12 h12" fill="#86EFAC" />
      <rect x="22" y="28" width="16" height="3" rx="1" fill="#F0FDF4" />
      <rect x="22" y="36" width="12" height="3" rx="1" fill="#DCFCE7" />
      <circle cx="46" cy="46" r="10" fill="#0EA5E9" />
      <path d="M41 46 l3.5 3.5 7-8" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </SvgShell>
  );
}

export function Icon3dFnf({ size = 40 } = {}) {
  return (
    <SvgShell title="Process F and F" size={size}>
      <circle cx="32" cy="36" r="20" fill="#0F766E" />
      <circle cx="32" cy="30" r="18" fill="#14B8A6" />
      <circle cx="32" cy="20" r="9" fill="#F0FDFA" />
      <path d="M18 44 c5-10 23-10 28 0" fill="#0D9488" />
      <path d="M48 12 l8-2 -2 8 6 5 -8 1 -2 8 -5-6 -8 2 2-8 -6-5 8-1 Z" fill="#F59E0B" />
    </SvgShell>
  );
}

/* —— Employee Actions —— */
export function Icon3dViewEmployee({ size = 40 } = {}) {
  return (
    <SvgShell title="View Employee" size={size}>
      <circle cx="32" cy="20" r="10" fill="#F472B6" />
      <path d="M12 54 c4-16 36-16 40 0" fill="#DB2777" />
      <circle cx="48" cy="44" r="12" fill="#38BDF8" />
      <circle cx="48" cy="44" r="5" fill="#E0F2FE" />
    </SvgShell>
  );
}

export function Icon3dLetter({ size = 40 } = {}) {
  return (
    <SvgShell title="Generate Letter" size={size}>
      <rect x="12" y="10" width="40" height="44" rx="4" fill="#6366F1" />
      <rect x="18" y="18" width="28" height="4" rx="1" fill="#E0E7FF" />
      <rect x="18" y="26" width="22" height="3" rx="1" fill="#C7D2FE" />
      <rect x="18" y="34" width="26" height="3" rx="1" fill="#C7D2FE" />
      <path d="M40 40 l10 4 -4 10 -3-6 -7 2 4-10 Z" fill="#FBBF24" />
    </SvgShell>
  );
}

export function Icon3dConfirm({ size = 40 } = {}) {
  return (
    <SvgShell title="Confirmation" size={size}>
      <circle cx="32" cy="32" r="22" fill="#059669" />
      <circle cx="32" cy="32" r="16" fill="#34D399" />
      <path d="M22 33 l7 7 14-16" fill="none" stroke="#ECFDF5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </SvgShell>
  );
}

export function Icon3dTransfer({ size = 40 } = {}) {
  return (
    <SvgShell title="Transfer" size={size}>
      <rect x="8" y="22" width="20" height="20" rx="3" fill="#0EA5E9" />
      <rect x="36" y="22" width="20" height="20" rx="3" fill="#0284C7" />
      <path d="M26 28 h12 M34 24 l6 8 -6 8" fill="none" stroke="#FDE68A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </SvgShell>
  );
}

export function Icon3dResign({ size = 40 } = {}) {
  return (
    <SvgShell title="Resignation" size={size}>
      <path d="M18 12 h20 l10 10 v30 H18 Z" fill="#DC2626" />
      <path d="M38 12 v10 h10" fill="#FCA5A5" />
      <path d="M26 34 h12 M32 28 v12" stroke="#FEE2E2" strokeWidth="3" strokeLinecap="round" />
    </SvgShell>
  );
}

/* —— Attendance Actions (compact set) —— */
export function Icon3dCalStatus({ size = 40 } = {}) {
  return (
    <SvgShell title="Change Status" size={size}>
      <rect x="10" y="12" width="44" height="40" rx="5" fill="#0D9488" />
      <rect x="10" y="12" width="44" height="12" fill="#14B8A6" />
      <circle cx="22" cy="36" r="4" fill="#FDE68A" />
      <circle cx="34" cy="36" r="4" fill="#F0FDFA" />
      <circle cx="46" cy="36" r="4" fill="#F0FDFA" />
    </SvgShell>
  );
}

export function Icon3dLeave({ size = 40 } = {}) {
  return (
    <SvgShell title="Leave" size={size}>
      <circle cx="32" cy="32" r="22" fill="#F59E0B" />
      <circle cx="32" cy="32" r="14" fill="#FDE68A" />
      <path d="M32 22 v12 l8 4" fill="none" stroke="#92400E" strokeWidth="3" strokeLinecap="round" />
    </SvgShell>
  );
}

export function Icon3dShift({ size = 40 } = {}) {
  return (
    <SvgShell title="Shift" size={size}>
      <path d="M12 40 L32 12 L52 40 Z" fill="#7C3AED" />
      <path d="M20 36 L32 18 L44 36 Z" fill="#A78BFA" />
      <circle cx="32" cy="40" r="6" fill="#EDE9FE" />
    </SvgShell>
  );
}

export function Icon3dPunch({ size = 40 } = {}) {
  return (
    <SvgShell title="Checks" size={size}>
      <rect x="14" y="10" width="36" height="44" rx="6" fill="#2563EB" />
      <circle cx="32" cy="28" r="10" fill="#93C5FD" />
      <circle cx="32" cy="28" r="4" fill="#EFF6FF" />
      <rect x="22" y="44" width="20" height="4" rx="1" fill="#BFDBFE" />
    </SvgShell>
  );
}

export function Icon3dLop({ size = 40 } = {}) {
  return (
    <SvgShell title="LOP Reversal" size={size}>
      <circle cx="32" cy="32" r="22" fill="#EA580C" />
      <path d="M22 32 h20 M32 22 v20" stroke="#FFEDD5" strokeWidth="4" strokeLinecap="round" />
      <path d="M40 18 a16 16 0 1 1 -16 0" fill="none" stroke="#FDBA74" strokeWidth="3" />
    </SvgShell>
  );
}

export function Icon3dBalance({ size = 40 } = {}) {
  return (
    <SvgShell title="Leave Balance" size={size}>
      <rect x="10" y="14" width="44" height="36" rx="5" fill="#0891B2" />
      <rect x="16" y="22" width="10" height="20" rx="2" fill="#A5F3FC" />
      <rect x="28" y="28" width="10" height="14" rx="2" fill="#67E8F9" />
      <rect x="40" y="18" width="10" height="24" rx="2" fill="#CFFAFE" />
    </SvgShell>
  );
}

export function Icon3dOd({ size = 40 } = {}) {
  return (
    <SvgShell title="On Duty" size={size}>
      <circle cx="32" cy="32" r="22" fill="#4F46E5" />
      <path d="M20 36 c6-12 20-12 26 0" fill="#818CF8" />
      <circle cx="32" cy="24" r="7" fill="#E0E7FF" />
      <path d="M44 18 l6 2 -2 6" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" />
    </SvgShell>
  );
}

export const PAYROLL_ACTION_ICONS = {
  assigned_structure: Icon3dStructure,
  salary_revision: Icon3dRevision,
  update_statutory: Icon3dStatutory,
  tax_planning: Icon3dTax,
  loan: Icon3dLoan,
  leave_encashment: Icon3dEncash,
  advance: Icon3dAdvance,
  reimbursement: Icon3dReimburse,
  process_fnf: Icon3dFnf,
};

export const EMPLOYEE_ACTION_ICONS = {
  view_employee: Icon3dViewEmployee,
  generate_letter: Icon3dLetter,
  confirmation: Icon3dConfirm,
  transfer: Icon3dTransfer,
  resignation: Icon3dResign,
};

export const ATTENDANCE_ACTION_ICONS = {
  change_status: Icon3dCalStatus,
  apply_leave: Icon3dLeave,
  change_shift: Icon3dShift,
  change_wo: Icon3dShift,
  regularise: Icon3dPunch,
  short_leave: Icon3dLeave,
  lop_reversal: Icon3dLop,
  leave_adjustment: Icon3dLeave,
  leave_balance: Icon3dBalance,
  comp_off_ledger: Icon3dBalance,
  view_edit_checks: Icon3dPunch,
  on_duty: Icon3dOd,
  overtime: Icon3dLop,
  restricted_holiday: Icon3dCalStatus,
};
